import { PageProps } from "@/types/page";
import { usePage } from "@inertiajs/react";
import classNames from "classnames";
import { formatInTimeZone, getTimezoneOffset } from "date-fns-tz";
import { TbCalendarOff } from "react-icons/tb";
import {
    AvailableTimesByDay,
    BLOCKED_TIMES_CONTAINER_ID,
    blockerKey,
    DayBlocker,
    dayKey,
    getConflictingBlockers,
} from "./dateTimePickerUtils";

type TimePickerProps = {
    day: Date;
    showRecurringOnly: boolean;
    availableTimesByDay: AvailableTimesByDay;
    dayBlockers: DayBlocker[];
    clientTimezone: string;
    onTimeSelect: (time: Date) => void;
    viewOnly?: boolean;
};

export default function TimePicker({
    day,
    availableTimesByDay,
    dayBlockers,
    clientTimezone,
    onTimeSelect,
    viewOnly = false,
}: TimePickerProps) {
    const { user } = usePage<PageProps>().props;
    const userTimezone = user?.timezone ?? "UTC";
    const isCoachView = user?.type === "coach" && !viewOnly;

    const availableTimes = availableTimesByDay[dayKey(day, userTimezone)] ?? [];

    const supportsHover =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(hover: hover)").matches;

    // Highlighting is done through direct DOM manipulation instead of React state
    // so that rapid mouse movement across the slots doesn't rerender the scheduler.
    // Only the data-highlighted attribute may be touched here: BlockedTimes owns
    // the classes and styles its items with data-highlighted: variants.
    const highlightBlockers = (keys: string[]) => {
        document
            .getElementById(BLOCKED_TIMES_CONTAINER_ID)
            ?.querySelectorAll<HTMLElement>("[data-blocker-key]")
            .forEach((element) => {
                if (keys.includes(element.dataset.blockerKey ?? "")) {
                    element.setAttribute("data-highlighted", "true");
                } else {
                    element.removeAttribute("data-highlighted");
                }
            });
    };

    return (
        <div className="flex flex-col gap-2 h-full">
            <div>
                <div
                    data-testid="time-picker-day-heading"
                    className="flex text-lg font-bold"
                >
                    {formatInTimeZone(day, userTimezone, "EEEE, MMMM d")}
                </div>
                <div className="text-sm text-brand-gray mr-4">
                    All times shown in your time (
                    {formatInTimeZone(new Date(), userTimezone, "zzz")})
                </div>
                {!isCoachView && <div>Pick a time</div>}
            </div>
            {availableTimes.length > 0 ? (
                <div className="grid grid-cols-2 gap-2">
                    {availableTimes.map((availableTime) => {
                        const conflictingBlockers = getConflictingBlockers(
                            availableTime,
                            dayBlockers,
                        );

                        const clientOffsetDiffersAtSlotTime =
                            isCoachView &&
                            getTimezoneOffset(userTimezone, availableTime) !==
                                getTimezoneOffset(
                                    clientTimezone,
                                    availableTime,
                                );

                        return (
                            <button
                                key={availableTime.toISOString()}
                                data-testid="time-slot"
                                disabled={viewOnly}
                                className={classNames(
                                    "flex items-center justify-center gap-2 w-full rounded-lg border border-brand-border p-2",
                                    !viewOnly && "hover:bg-brand-lightGray",
                                )}
                                onClick={() => onTimeSelect(availableTime)}
                                onMouseEnter={
                                    supportsHover
                                        ? () =>
                                              highlightBlockers(
                                                  conflictingBlockers.map(
                                                      blockerKey,
                                                  ),
                                              )
                                        : undefined
                                }
                                onMouseLeave={
                                    supportsHover
                                        ? () => highlightBlockers([])
                                        : undefined
                                }
                            >
                                {isCoachView ? (
                                    <div className="flex flex-col">
                                        <div className="flex items-center justify-center gap-1">
                                            {formatInTimeZone(
                                                availableTime,
                                                userTimezone,
                                                "h:mm a",
                                            )}
                                            {!!conflictingBlockers.length && (
                                                <TbCalendarOff
                                                    data-testid="slot-conflict-indicator"
                                                    aria-label="Conflicts with your schedule"
                                                    className="shrink-0 text-brand-dangerOrange -my-3"
                                                    size={18}
                                                />
                                            )}
                                        </div>
                                        {clientOffsetDiffersAtSlotTime && (
                                            <div className="text-xs text-brand-decoBlue">
                                                {formatInTimeZone(
                                                    availableTime,
                                                    clientTimezone,
                                                    "h:mm a zzz",
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    formatInTimeZone(
                                        availableTime,
                                        userTimezone,
                                        "h:mm a",
                                    )
                                )}
                            </button>
                        );
                    })}
                </div>
            ) : (
                <div className="flex items-center justify-center grow">
                    <div className="text-brand-gray">
                        No available times for this day.
                    </div>
                </div>
            )}
        </div>
    );
}
